import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Building2, User, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { HtmlContent } from '../components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DernekDetay() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [assoc, setAssoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/associations/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(data => { setAssoc(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50">
        <div className="text-amber-700 text-lg animate-pulse">Yükleniyor...</div>
      </div>
    );
  }

  if (error || !assoc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 gap-4">
        <h2 className="text-2xl text-amber-900 font-semibold">Dernek bulunamadı</h2>
        <Button onClick={() => navigate('/derneklerimiz')} variant="outline" className="border-amber-300 text-amber-900">
          <ChevronLeft className="w-4 h-4 mr-2" /> Derneklerimize Dön
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/derneklerimiz')}
          variant="ghost"
          className="mb-6 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
          data-testid="dernek-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" /> Derneklerimiz
        </Button>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden mb-8">
          {assoc.image_url && (
            <div className="w-full h-72">
              <img src={assoc.image_url} alt={assoc.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }} data-testid="dernek-title">
                  {assoc.name}
                </h1>
              </div>
            </div>

            <div data-testid="dernek-about">
              <HtmlContent html={assoc.about} className="text-amber-800 leading-relaxed" />
            </div>
          </div>
        </div>

        {/* Başkan */}
        <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-orange-500" />
            Dernek Başkanı
          </h2>
          <div className="flex items-center gap-4">
            {assoc.president_image ? (
              <img src={assoc.president_image} alt={assoc.president_name} className="w-20 h-20 rounded-full object-cover border-4 border-amber-200" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <p className="text-xl font-semibold text-amber-900" data-testid="dernek-president">{assoc.president_name}</p>
              <p className="text-amber-600 text-sm">Dernek Başkanı</p>
            </div>
          </div>
        </div>

        {/* Yönetim Kurulu */}
        {assoc.board_members && assoc.board_members.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-6">
            <h2 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Yönetim Kurulu
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {assoc.board_members.map((member, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg" data-testid={`board-member-${index}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-900 text-sm">{member.name}</p>
                    <p className="text-amber-600 text-xs">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
